import { t } from 'i18next';
import iframeResize from 'iframe-resizer'; // We must use the 4.4.5 version of iframe-resizer to avoid license issues
import React, { useCallback, useEffect, useState } from 'react';

import { enqueueBasicAlert } from 'Pages/AlertManager';
import { requestCampaignsDashboard } from 'Store/entities/analytics/analytics.api';

import AudienceSelect from '../Filters/AudienceSelect/AudienceSelect';
import CampaignSelect from '../Filters/CampaignSelect/CampaignSelect';
import DateRangeInput from '../Filters/DateRangeInput/DateRangeInput';
import SelectedItemsDisplay from '../Filters/SelectedItemsDisplay/SelectedItemsDisplay';
import useFilters, {
  type CampaignsFilter,
  type StartDateFilter,
  type EndDateFilter,
  type AudienceGroupsFilter,
} from '../Filters/useFilters.hook';
import styles from './Campaigns.style';

export type DashboardCampaignsFilters = CampaignsFilter & StartDateFilter & EndDateFilter & AudienceGroupsFilter;

const ALERT_DURATION = 5000;

const Campaigns: React.FC = () => {
  const [campaignsDashboardUrl, setCampaignsDashboardUrl] = useState<string | null>(null);

  const { selectedFilters, filters, handleFilterChange, handleDeleteFilter } = useFilters<DashboardCampaignsFilters>();

  const fetchCampaignsDashboard = useCallback(
    async (filters: DashboardCampaignsFilters) => {
      try {
        const result = await requestCampaignsDashboard(filters);
        setCampaignsDashboardUrl(result.url);
      } catch (error) {
        enqueueBasicAlert(
          {
            id: 'analytics-campaigns-error',
            title: t('analytics:dashboard.campaigns.errors.main_dashboard.title'),
            text: t('analytics:dashboard.campaigns.errors.main_dashboard.text'),
            status: 'error',
            icon: 'error',
          },
          ALERT_DURATION,
        );
      }
    },
    [filters],
  );

  useEffect(() => {
    fetchCampaignsDashboard(filters);
  }, [filters]);

  useEffect(() => {
    if (campaignsDashboardUrl) {
      iframeResize.iframeResizer(
        {
          autoResize: true,
          sizeHeight: true,
        },
        '#campaigns-dashboard',
      );
    }
  }, [campaignsDashboardUrl]);

  return (
    <div style={styles.container}>
      <div style={styles.headerContainer}>
        <h1 style={styles.title}>{t('analytics:dashboard.campaigns.title')}</h1>

        <div style={styles.filtersContainer}>
          <div style={styles.inputsContainer}>
            <DateRangeInput
              startDate={filters.startDate}
              endDate={filters.endDate}
              handleFilterChange={handleFilterChange}
            />

            <CampaignSelect selectedCampaignIds={filters.campaigns} handleSelectedItems={handleFilterChange} />

            <AudienceSelect
              selectedAudienceGroupsIds={filters.audienceGroups}
              handleSelectedItems={handleFilterChange}
            />
          </div>

          <SelectedItemsDisplay selectedFilters={selectedFilters} handleDeleteFilter={handleDeleteFilter} />
        </div>
      </div>

      {campaignsDashboardUrl && (
        <div style={styles.statisticsContainer}>
          <iframe id="campaigns-dashboard" src={campaignsDashboardUrl} style={styles.iframeContainer} />
        </div>
      )}
    </div>
  );
};

export default Campaigns;
